/**
 * The drag-and-drop board — where a website enquiry is worked.
 *
 * Nearly everyone here asked for something through the site and was picked up
 * by the visitor tracker: a demo, a quote, a contact form, a chatbot
 * conversation, a WhatsApp click. Which of those reach the board is a setting
 * on the server, and every row says which kind it is and whether the form
 * actually went through — the two things the Enquiry and Form filters narrow
 * by. The rest were added by hand — "Add manual", or a CSV — and carry the
 * source "Manual" so they can be told apart. Registrations are clients and
 * live in the Clients module; a deal typed in on the Deals page is a deal
 * already and lives there.
 *
 * Dropping a card onto a column moves it, stamps a close date when it lands in
 * Lost, and logs the move as an activity so the "last touch" figures stay
 * honest. There is no Won column: winning is the tick on the card, and it
 * converts the row — it becomes a deal, leaves this board, and appears on the
 * Deals page, keeping its id, its company and every activity already logged
 * against it. A column that emptied itself the moment anything landed in it
 * only ever looked broken.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, formatDate, daysAgo } from '../lib/dates.js';
import api from '../lib/api.js';
import { download } from '../lib/format.js';
import Icon from '../lib/icons.jsx';
import StageNavigator from '../components/StageNavigator.jsx';
import CrmFilter from '../components/CrmFilter.jsx';
import PipelineGrid from '../components/PipelineGrid.jsx';
import PipelineCards from '../components/PipelineCards.jsx';
import ViewToggle from '../components/ViewToggle.jsx';
import {
  filterDeals, boardRows, isWorking, companyName, leadDetails, lastTouch,
  leadWhen, isStalled, DISPLAY_STAGES, subStatusesOf, displayStage, subStatus
} from '../domain/crm.js';

export default function Pipeline() {
  const {
    S, crm, rows, cF, setCF, crmMode, pipeline, leads, setSheet, toast, syncTracker,
    managesBoard, me
  } = useApp();

  /* The workspace's stages, from settings (DB-driven). The constant is only a
     pre-settings fallback so nothing paints empty. */
  const STAGES = (S.stages && S.stages.length) ? S.stages : DISPLAY_STAGES;

  /* Who is holding it — one scope control: All · Unassigned · Mine. Replaces
     the old My-leads/Whole-team toggle and the Assigned/Unassigned/dropped
     tabs. Page state, not a filter, so it never leaks into an export's label. */
  const [scope, setScope] = useState('all');

  /* One stage picked on the tiles: the board and the table show it alone. */
  const stageTab = cF.stage || 'All';

  /*
   * The single table, as one board (build decision 2026-10): open leads, won
   * and lost deals, and turned-away rows — everything in the deals table except
   * product registrations. Grouped below by the five display stages, so New ·
   * Demo · Proposal · Won · Lost are all one board. The stage is cleared before
   * filtering; the stage split is done locally against displayStage().
   */
  const base = useMemo(
    () => filterDeals(crm, { ...cF, stage: '' }, 'board', rows),
    [crm, cF, rows]
  );

  const unowned = base.filter(d => !(d.owner || '').trim());
  const mineRows = (me && me.name) ? base.filter(d => d.owner === me.name) : [];
  const handed = scope === 'unassigned' ? unowned
    : scope === 'mine' ? mineRows
      : base;

  /* The board groups by the five display stages, mapped from the underlying
     stage by displayStage(); a chosen sub-status narrows further. */
  const shownRows = (cF.stage ? handed.filter(d => displayStage(d) === cF.stage) : handed)
    .filter(d => !cF.subStatus || subStatus(d) === cF.subStatus);

  const groups = STAGES.map(stage => {
    const deals = handed.filter(d => displayStage(d) === stage
      && (!cF.subStatus || subStatus(d) === cF.subStatus));
    return { stage, deals, value: deals.reduce((t, d) => t + (+d.value || 0), 0) };
  });

  /*
   * The board as a laid-out workbook.
   *
   * It was a CSV written here, which cannot carry a column width, a colour
   * or a heading - so it opened as a wall of grey with the dates rendered
   * ########. The rows go to the server, which lays them out with the same
   * styling as the demo report. They are sent rather than a filter, so what
   * comes out is the stage, the filters and the order in front of whoever
   * pressed the button.
   *
   * Import is gone: every row here arrives from the tracker or the Add
   * manual box, so it was a third door onto the same table, unused.
   */
  /*
   * One row, two documents.
   *
   * The workbook and the PDF were built from different shapes, so a column
   * added to one quietly never reached the other. They take the same rows
   * now, off `shownRows` — which is the board with every filter applied,
   * stage included. Exporting what is not on screen is the one thing an
   * export must never do.
   */
  const reportRows = () => shownRows.map(d => {
    const f = leadDetails(crm, d);
    const touch = lastTouch(crm, d.id);
    const when = parseDate(leadWhen(d));
    return {
      title: d.title,
      client: '',
      free: false,
      email: f.email || '',
      phone: f.phone || '',
      company: companyName(crm, d.cid) || '',
      city: f.city || '',
      country: f.country || '',
      source: d.source || '',
      campaign: f.campaign || '',
      plan: f.productList.join(', ') || d.plan || '',
      cycle: d.cycle || '',
      value: '',
      wonOn: when ? formatDate(when, false, S.dateFmt) : '',
      owner: d.owner || '',
      manual: !d.origin,
      stage: d.stage,
      stale: isStalled(S, crm, d),
      quiet: touch ? `${daysAgo(touch)}d` : (when ? `${daysAgo(when)}d, never touched` : ''),
      arrived: when ? formatDate(when, false, S.dateFmt) : '',
      landing: f.landingPath || '',
      leadType: f.type || '',
      leadState: f.stateLabel || '',
      referrer: f.referrer || '',
      utmSource: f.utm.source || '',
      utmMedium: f.utm.medium || '',
      device: [f.device, f.os, f.browser].filter(Boolean).join(' · '),
      visits: f.visits || '',
      notes: d.notes || ''
    };
  });

  /* What the filters currently say, in words, so a document can carry the
     question it answers rather than arriving as an unlabelled list. */
  const filterLabel = () => [
    cF.q && `"${cF.q}"`,
    cF.stage && `stage ${cF.stage}`,
    cF.leadType && `kind ${cF.leadType.split(',').join(' or ')}`,
    cF.leadState && `form ${cF.leadState.split(',').map(x => (x === 'submitted' ? 'submitted' : 'partially submitted')).join(' or ')}`,
    cF.source && `source ${cF.source.split(',').join(' or ')}`,
    cF.customer && (cF.customer === 'client' ? 'already clients' : 'not clients yet'),
    cF.owner && `owner ${cF.owner}`,
    cF.campaign && `campaign ${cF.campaign}`,
    (cF.from || cF.to) && `${cF.from || '…'} to ${cF.to || '…'}`
  ].filter(Boolean).join(' · ') || 'no filters';

  const [saving, setSaving] = useState(false);
  const [pdfing, setPdfing] = useState(false);

  const onPdf = async () => {
    if (pdfing) return;
    setPdfing(true);
    try {
      const rowsOut = reportRows();
      /*
       * The table, and nothing above it.
       *
       * It carried a row of headline figures and three breakdowns, and both
       * came out empty - the shapes the PDF builder wants are not the ones
       * that were being handed to it, so the page opened with four zeroes and
       * "Nothing in this period" three times over a table of 1,366 rows. A
       * summary that reads zero above real data is worse than no summary: it
       * is the first thing anybody looks at and it is wrong.
       *
       * The filters are still named under the heading, which is the part that
       * says what the document is.
       */
      const blob = await api.deals.reportPdf({
        kind: 'pipeline',
        rowsTitle: 'Every enquiry',
        generatedAt: formatDate(new Date(), true, S.dateFmt),
        period: { label: filterLabel() },
        rows: rowsOut
      });
      download(blob, `xyra-pipeline-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast(`${rowsOut.length} rows as PDF`);
    } catch (e) {
      toast(e.message || 'That report could not be made');
    } finally {
      setPdfing(false);
    }
  };

  const onExport = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        generatedAt: formatDate(new Date(), true, S.dateFmt),
        title: 'XYRA - pipeline',
        filters: filterLabel(),
        deals: reportRows()
      };
      const blob = await api.deals.book(payload);
      download(blob, `xyra-pipeline-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast(`Exported ${payload.deals.length} rows`);
    } catch (e) {
      toast(e.message || 'That export could not be made');
    } finally {
      setSaving(false);
    }
  };

  /*
   * Pull from the tracker now.
   *
   * The server already does this every minute, so the button is not how leads
   * normally arrive — it is for the minute somebody has been told an enquiry
   * came in and is watching the board for it. It takes fifteen to twenty
   * seconds, all of it the tracker, so it says what it is doing rather than
   * looking like a button that did nothing.
   */
  const [pulling, setPulling] = useState(false);
  const onSync = async () => {
    if (pulling) return;
    setPulling(true);
    try {
      const out = await syncTracker();
      const n = (out && (out.created || 0)) + (out && (out.won || 0));
      toast(n
        ? `${n} new from the tracker`
        : `Up to date — ${(out && out.updated) || 0} refreshed`);
    } catch (e) {
      toast(e.message || 'The tracker could not be reached');
    } finally {
      setPulling(false);
    }
  };

  const tools = (
    <>
      {/* Sync and Bulk upload now live on the title row; Import was a duplicate
          of Bulk upload and is gone. What is left here is the two exports, each
          in its own format's colour — a spreadsheet is green and a PDF is red
          everywhere else on a desk. */}
      <button
        type="button"
        className="btn fmtbtn xlsx"
        onClick={onExport}
        disabled={saving}
        title="Download exactly what the filters have left, as a formatted Excel workbook"
      >
        <Icon name={saving ? 'refresh' : 'sheet'} />{saving ? 'Preparing…' : 'Excel'}
      </button>
      <button
        type="button"
        className="btn fmtbtn pdf"
        onClick={onPdf}
        disabled={pdfing}
        title="The same rows, laid out for paper"
      >
        <Icon name={pdfing ? 'refresh' : 'file'} />{pdfing ? 'Preparing…' : 'PDF'}
      </button>
    </>
  );

  /* One stage selected: both the table and the board show that stage alone. A
     tab whose stage has since emptied falls back rather than showing nothing. */
  const picked = groups.find(g => g.stage === stageTab);
  const shown = picked ? [picked] : groups;
  const count = list => list.reduce((t, g) => t + g.deals.length, 0);

  const onBoard = boardRows(crm, rows);
  const working = onBoard.filter(isWorking);

  return (
    <>
      {/* Pipeline header (redesign): clean title + count with the primary
          actions on the right. Replaces the global page header on this page,
          which is hidden via CSS for the pipeline view. */}
      <div className="pipehead">
        <div className="pipehead-tt">
          <h2>Pipeline</h2>
          <p>{base.length.toLocaleString()} leads across {STAGES.length} stages</p>
        </div>
        <div className="pipehead-acts">
          {/* Sync is a compact icon — a whole-workspace action for whoever hands
              leads out; it also runs on a cron. */}
          {managesBoard ? (
            <button
              type="button" className="btn icon"
              onClick={onSync}
              disabled={pulling}
              aria-label="Sync now" title="Fetch new enquiries from the visitor tracker now"
            >
              <Icon name="refresh" className={pulling ? 'spinning' : undefined} />
            </button>
          ) : null}
          <button
            type="button" className="btn"
            onClick={() => setSheet({ kind: 'importLeads' })}
            title="Add many leads from a spreadsheet"
          >
            <Icon name="up" />Bulk upload
          </button>
          <button
            type="button" className="btn primary"
            onClick={() => setSheet({ kind: 'deal', manual: true })}
            title="Add a single lead"
          >
            <Icon name="plus" />New lead
          </button>
        </div>
      </div>

      {/*
        * Stage navigator (redesign, docs/ui-redesign-spec.md §3): one tile per
        * stage with its live count, which doubles as the stage filter. It stands
        * in for the old summary-tile row — on a board worked stage by stage, the
        * count a manager reads and the stage they jump to are the same thing.
        */}
      <StageNavigator
        stages={STAGES}
        groups={groups}
        selected={cF.stage}
        onPick={stage => setCF(f => ({ ...f, stage, subStatus: '' }))}
      />

      {/* Sub-status pills for the chosen stage (brief §6): All, then that
          stage's sub-statuses, each narrowing the table below. */}
      {cF.stage && subStatusesOf(S, cF.stage).length ? (
        <div className="substatus-pills" role="tablist" aria-label={`Status inside ${cF.stage}`}>
          <button
            type="button" role="tab" aria-selected={!cF.subStatus}
            className={`subpill${cF.subStatus ? '' : ' on'}`}
            onClick={() => setCF(f => ({ ...f, subStatus: '' }))}
          >
            All
          </button>
          {subStatusesOf(S, cF.stage).map(x => {
            const n = handed.filter(d => displayStage(d) === cF.stage && subStatus(d) === x).length;
            return (
              <button
                key={x} type="button" role="tab" aria-selected={cF.subStatus === x}
                className={`subpill${cF.subStatus === x ? ' on' : ''}`}
                onClick={() => setCF(f => ({ ...f, subStatus: f.subStatus === x ? '' : x }))}
              >
                {x}<em>{n}</em>
              </button>
            );
          })}
        </div>
      ) : null}

      <CrmFilter kind="pipeline" tools={tools} />

      {/* One flat control strip, not a boxed card header: who-holds-it tabs on
          the left, the board/table switch on the right. The grid draws its own
          frame below, so a second card around it would double-box the view. */}
      <div className="pipe-strip">
        {/* Who is holding it — All · Unassigned · Mine (one scope control). */}
        <div className="handtabs" role="tablist" aria-label="Who is holding it">
          {[['all', 'All', base.length],
            ['unassigned', 'Unassigned', unowned.length],
            ...(me && me.name ? [['mine', 'Mine', mineRows.length]] : [])].map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={scope === key}
                className={scope === key ? 'on' : undefined}
                onClick={() => setScope(key)}
              >
                {label}<b>{n}</b>
              </button>
            ))}
        </div>
        <ViewToggle view="pipeline" />
      </div>

      {/* Bulk assign returns as a selection bar driven by a checkbox column in
          the grid; the old always-on "Rows _ to _ · Tick" strip is gone. */}
      {crmMode.pipeline === 'list'
        ? <PipelineGrid groups={shown} hideTabs />
        : <PipelineCards groups={shown} />}
    </>
  );
}
